import { useRef } from 'react';
import api from 'api';
import { getResizedImage } from 'shared-helpers/common';
import { useDispatch, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { waitForImage } from 'lib/files';
import { Alert, Button, ModalButtonWithForm } from 'components/common';
import { DisplayField, FormGroup, Select } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';
import { HomePageSettingsBannerPreview } from '.';

import type { FormApi } from 'informed';
import type { Theme } from 'shared-types';

const HomePageSettingsBannerThemes = () => {
  const uploader = useRef<any>(undefined);
  const formApi = useRef<FormApi<any>>(undefined);

  const { dispatch } = useDispatch();
  const { messages, serverConfig } = useReduxState();

  const { data: themesData, updateData: updateThemesData } = useApiData({
    url: '/admin/server-themes?type=site'
  });

  const themes =
    themesData?.themes?.filter(
      (theme) =>
        theme.id === serverConfig.customThemeId ||
        theme.data.enableWithAttributeConditions?.enabled
    ) || [];

  const themesById = themes.reduce((themesById, theme) => {
    themesById[theme.id] = theme;
    return themesById;
  }, {} as Record<string, Theme>);

  const bannerImageField = 'homePageBannerImageForTheme';
  const bannerLayoutField = 'homePageBannerImageLayoutForTheme';
  const bannerPositionField = 'homePageBannerImagePositionForTheme';

  const getUploader = (fileUploader: any) => {
    uploader.current = fileUploader;
  };

  const handleRemove = () => {
    uploader.current?.reset();
    formApi.current.setValue(bannerImageField, null);
  };

  const handleSubmit = async (values) => {
    if (uploader.current?.state.file) {
      const file = await uploader.current.upload();
      if (file) {
        values[bannerImageField] = file.url;
        // wait for resized image to be available
        await waitForImage(getResizedImage({ size: 1920, url: file.url }));
      }
    }
    if (!values[bannerImageField]) values[bannerImageField] = null;

    const themeId = values.bannerImageThemeId;

    const data = {
      homePageBannerImage: values[bannerImageField],
      homePageBannerImageLayout: values[bannerLayoutField],
      homePageBannerImagePosition: values[bannerPositionField]
    };

    await api.put(`/admin/server-themes/${themeId}`, { data });

    await updateThemesData();

    dispatch.addNotice({
      text: messages.homePageSettingsBannerThemeUpdated,
      type: 'success'
    });
  };

  const handleThemeChange = (themeId) => {
    const targetTheme = themes.find((theme) => theme.id === themeId);
    formApi.current.setValue(
      bannerImageField,
      targetTheme.data.homePageBannerImage
    );
    formApi.current.setValue(
      bannerLayoutField,
      targetTheme.data.homePageBannerImageLayout || 'full-width'
    );
    formApi.current.setValue(
      bannerPositionField,
      targetTheme.data.homePageBannerImagePosition || 'center center'
    );
  };

  const handleThumbnailGenerated = ({ preview }) => {
    formApi.current.setValue(bannerImageField, preview);
  };

  if (themes.length <= 1) return null;

  return (
    <div className="banner-themes">
      <Alert
        text={
          <div className="info-content">
            {messages.homePageSettingsBannerThemeInfo}

            <ModalButtonWithForm
              buttonCategory="light"
              buttonSize="small"
              buttonText={messages.commonEdit}
              initialValues={{ bannerImageThemeId: themes[0].id }}
              modalActionCategory="success"
              modalActionText={messages.commonSave}
              modalTitle={messages.homePageSettingsBanner}
              modalWidth="wide"
              onSubmit={handleSubmit}
              preventSubmit={true}
              setFormApi={(informedApi: FormApi) =>
                (formApi.current = informedApi)
              }
              stopPropagation={true}
            >
              {({ values }) => (
                <div className="inner">
                  <FormGroup label={messages.readerSettingsTheme}>
                    <Select
                      field="bannerImageThemeId"
                      id="banner-image-theme-id"
                      onChange={handleThemeChange}
                      options={themes.map((theme) => ({
                        label: theme.title,
                        value: theme.id
                      }))}
                      style={{ maxWidth: 300 }}
                    />
                  </FormGroup>

                  {themesById[values.bannerImageThemeId] ? (
                    <div className="banner">
                      <DisplayField
                        initialValue={
                          themesById[values.bannerImageThemeId]?.data
                            ?.homePageBannerImage
                        }
                        field={bannerImageField}
                      />

                      <div
                        className="uploader"
                        style={{
                          display: values[bannerImageField] ? 'none' : 'block'
                        }}
                      >
                        <FileUploader
                          allowedFileTypes={['image/*']}
                          dragDrop={true}
                          dragDropActions={false}
                          getUploader={getUploader}
                          icon="file-image"
                          id="home-page-banner-image-uploader-theme"
                          infoText={messages.homePageSettingsBannerInfoText}
                          keyPrefix="uploads/images/original/"
                          onThumbnailGenerated={handleThumbnailGenerated}
                          placeholder={
                            messages.homePageSettingsBannerPlaceholder
                          }
                          uuidAsFilename={true}
                        />
                      </div>

                      {values[bannerImageField] ? (
                        <div className="banner-image">
                          <HomePageSettingsBannerPreview
                            bannerImageValue={
                              values[bannerImageField] ||
                              themesById[values.bannerImageThemeId]?.data
                                ?.homePageBannerImage
                            }
                            bannerLayoutField={bannerLayoutField}
                            bannerLayoutValue={
                              values[bannerLayoutField] ||
                              themesById[values.bannerImageThemeId]?.data
                                ?.homePageBannerImageLayout ||
                              'full-width'
                            }
                            bannerPositionField={bannerPositionField}
                            bannerPositionValue={
                              values[bannerPositionField] ||
                              themesById[values.bannerImageThemeId]?.data
                                ?.homePageBannerImagePosition ||
                              'center center'
                            }
                            id="home-page-theme-banner"
                          />
                        </div>
                      ) : null}

                      <div className="remove">
                        <Button
                          category="light"
                          disabled={!values[bannerImageField]}
                          icon="trash-alt"
                          onClick={handleRemove}
                          text={messages.commonRemove}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </ModalButtonWithForm>
          </div>
        }
        type="warning-light"
      />

      <style jsx>{`
        .banner-themes {
          margin-top: 25px;
        }
        .info-content {
          align-items: flex-start;
          display: flex;
          gap: 20px;
        }
        .banner {
          margin-top: 20px;
        }
        .uploader {
          max-width: 540px;
        }
        .remove {
          margin-top: 25px;
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsBannerThemes;
